import {isArray, isNil} from "lodash";
import AnimalTypes from "@wesstron/utils/Api/constants/animalTypes";
import {filterMap} from "./Utils";
import {myID} from "../libs/generateID";
import AggrTypes from "../queries/technogroup-result-details/AggrTypes";
import DataTypes from "../queries/technogroup-result-details/DataTypes";
import MapDisplayTypes from "../queries/technogroup-result-details/MapDisplayTypes";

/**
 * use this helper function to check if a farm map is able to display custom animals
 * @param animalList
 * @param allowGroupsInsideCustomAnimalList
 * @return {*|boolean}
 */
export function validateCustomFarmAnimals(
    animalList,
    allowGroupsInsideCustomAnimalList = false,
    magicAnimalsFromPlacement = false
) {
    if (!isArray(animalList) || animalList.length === 0) return false;

    const requiredFieldsForSingleAnimal = [
        "AnmID",
        "AnmNo1",
        "PlcmntID",
        "AnimalKind",
    ];
    const requiredFieldsForGroup = ["AnmGrp", "GroupNumber", "PlcmntID"];

    return animalList.every((animal) => {
        if (magicAnimalsFromPlacement) {
            return animal.hasOwnProperty("PlcmntID");
        } else if (
            allowGroupsInsideCustomAnimalList &&
            animal.hasOwnProperty("AnmGrp")
        ) {
            // Validate as group
            return requiredFieldsForGroup.every((field) => {
                if (!animal.hasOwnProperty(field)) {
                    console.warn(
                        `FarmMapComponent: Missing required group field '${field}' in custom animal object: ${JSON.stringify(animal)}`
                    );
                    return false;
                }
                return true;
            });
        } else {
            // Validate as single animal
            return requiredFieldsForSingleAnimal.every((field) => {
                if (!animal.hasOwnProperty(field)) {
                    console.warn(
                        `FarmMapComponent: Missing required field '${field}' in custom animal object: ${JSON.stringify(animal)}`
                    );
                    return false;
                }
                return true;
            });
        }
    });
}

/**
 * Przekształca listę zwierząt na potrzeby wyświetlania mapy fermy.
 *
 * Funkcja została napisana specjalnie na potrzeby widoku wyników,
 * gdzie to samo zwierzę (lub grupa) może pojawić się wielokrotnie.
 * Mapa fermy wymaga, aby każde zwierzę miało unikalny identyfikator (AnmID),
 * dlatego ta funkcja wymusza unikalność ID poprzez dodawanie przyrostków (_1, _2 itd.).
 *
 * UWAGA: może to wprowadzać niespójność w przypadku logiki opartej na identyfikatorach,
 * np. onClick zwróci zmodyfikowane (nieoryginalne) ID.
 *
 * @param {Array} animalList - lista zwierząt (indywidualnych i grup).
 * @param magicAnimalsFromPlacement
 * @param allowGroupsInsideCustomAnimalList
 * @returns {Array} - zmodyfikowana lista z unikalnymi AnmID.
 */
export function farmMapTransformAnimals(
    animalList,
    {
        magicAnimalsFromPlacement = false,
        allowGroupsInsideCustomAnimalList = false,
    } = {}
) {
    if (!isArray(animalList)) return animalList;

    const idMap = {}; // przechowuje licznik użyć każdego oryginalnego ID

    const activeIdentificationKeys = filterMap(
        [
            ["AnmID", true],
            ["AnmGrp", !!allowGroupsInsideCustomAnimalList],
            // ["PlcmntID", !!magicAnimalsFromPlacement] <- celowo pominięte, bo będzie konfliktować z logiką komór
        ],
        ([key, enabled]) => (enabled ? key : null)
    );

    console.log("transformANIMALS", animalList);

    return animalList.map((animal, index) => {
        const identificationKeyUsed = activeIdentificationKeys.find((key) =>
            animal.hasOwnProperty(key)
        );
        const originalId = animal[identificationKeyUsed] ?? `mgk_${myID()}`;
        let uniqueId = originalId;

        if (idMap[originalId] !== undefined) {
            uniqueId = `${originalId}_${idMap[originalId]}`;
            idMap[originalId]++;
        } else {
            idMap[originalId] = 1;
        }

        const newAnimal = {
            _originalIndex: index,
            _originalAnmID: animal.AnmID,
            ...animal,
            AnmID: uniqueId,
        };

        if (isNil(animal.AnmNo1)) {
            if ("GroupNumber" in animal) {
                newAnimal.AnmNo1 = animal.GroupNumber;
            } else if ("AnmID" in animal) {
                newAnimal.AnmNo1 = animal.AnmID;
            } else if ("PlcmntID" in animal) {
                newAnimal.AnmNo1 = animal.PlcmntID;
            }
        }

        if (isNil(animal.AnimalKind)) {
            if ("AnmGrp" in animal) {
                newAnimal.AnmNo1 = AnimalTypes.PORKER;
            } else {
                newAnimal.AnmNo1 = AnimalTypes.SOW;
            }
        }

        return newAnimal;
    });
}

export function getAttributeTypes(animals) {
    const types = {};

    animals.forEach((animal) => {
        Object.entries(animal).forEach(([key, value]) => {
            let type;

            if (value === null || value === undefined) {
                type = "nil";
            } else if (Array.isArray(value)) {
                type = "array";
            } else if (typeof value === "number") {
                type = Number.isInteger(value) ? "integer" : "float";
            } else if (typeof value === "string") {
                type = "string";
            } else if (typeof value === "object") {
                type = "object";
            } else {
                type = "nil";
            }

            // Always prefer a more specific type over "nil"
            if (!(key in types) || types[key] === "nil") {
                types[key] = type;
            }
        });
    });

    return types;
}

const SKIP_KEYS = new Set([
    "PlcmntID",
    "AnmNo1",
    "AnmID",
    "AnimalKind",
    "dataEv",
]);

export function generateMultipleHeaders(
    attributeTypes,
    {skipKeys = SKIP_KEYS} = {}
) {
    const headers = [];

    for (const [key, type] of Object.entries(attributeTypes)) {
        if (skipKeys.has(key)) continue;

        if (type === "integer" || type === "float" || type === "number") {
            // Add multiple aggregation headers for numbers
            const aggrs = [
                AggrTypes.SUM,
                AggrTypes.AVERAGE,
                AggrTypes.MIN,
                AggrTypes.MAX,
                AggrTypes.COUNT_DISTINCT,
            ];

            aggrs.forEach((aggregate) => {
                headers.push({
                    name: key,
                    field: key,
                    type: DataTypes.NUMBER,
                    aggregate,
                    displayExclusivelyOnMap: true,
                    mapDisplayType: MapDisplayTypes.HEAT_MAP,
                    ...(aggregate === AggrTypes.AVERAGE && {
                        aggregateOptions: {fixed: 1},
                    }),
                });
            });
        } else if (type === "string") {
            // Just one header with category mapping for strings
            headers.push({
                name: key,
                field: key,
                type: DataTypes.STRING,
                displayExclusivelyOnMap: true,
                mapDisplayType: MapDisplayTypes.CATEGORY,
            });
        }
        // skip other types
    }

    return headers;
}
